/**
 * Persistence and application of the colour-scheme preference.
 *
 * The preference lives in `localStorage`, not `sessionStorage` as the affiliate
 * choice does: a theme is a durable personal setting, not a per-session one. It
 * is deliberately not namespaced per user -- the theme belongs to the device the
 * person is sitting at, and it must be readable by the inline script in
 * index.html that runs before we know who is signed in.
 *
 * Every access is guarded because storage throws in private-mode and sandboxed
 * contexts; a failure degrades to "follow the system" rather than breaking the app.
 *
 * The key and the applied class name are mirrored by that inline script. Change
 * one, change the other.
 */

/** What the user asked for. `system` defers to the OS colour scheme. */
export type ThemePreference = "light" | "dark" | "system";

/** What is actually painted, once `system` has been resolved. */
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "admin-v2.theme";

const DARK_CLASS = "dark";

function isThemePreference(value: string): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

/** The stored preference, or `system` when there is none or storage is unusable. */
export function readStoredTheme(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored !== null && isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

/** Persists the preference. No-ops when storage is unavailable. */
export function writeStoredTheme(theme: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage is unavailable (private mode, sandboxed iframe). The choice still
    // applies for this page's lifetime; it just will not survive a reload.
  }
}

/** The OS colour scheme. Defaults to light where `matchMedia` is unavailable. */
export function systemTheme(): ResolvedTheme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches === true
    ? "dark"
    : "light";
}

/** Resolves a preference to the scheme that should actually be painted. */
export function resolveTheme(theme: ThemePreference): ResolvedTheme {
  return theme === "system" ? systemTheme() : theme;
}

/** Applies the resolved scheme by toggling the `.dark` class on <html>. */
export function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.classList.toggle(DARK_CLASS, resolved === "dark");
}
